/**
 * **Mängel im Portal — die reine Logik** hinter `DefectsPanel` und `DefectDetailDialog`: welcher
 * Filter welche Abfrage wird, welche Knöpfe jemand sieht, und was eine Aktion an den Server
 * schickt. Ohne React, damit es sich prüfen lässt.
 *
 * Die Regeln spiegeln `applyDefectAction` in tri-server (src/defects/defectLifecycle.ts). Der
 * Server prüft trotzdem selbst (403/409) — die Spiegelung entscheidet nur, was gezeigt wird.
 */

import type { Defect, DefectAction, DefectOperation, DefectSeverity, DefectStatus, MeasureKind } from "../../DefectsAPI";

export const SEVERITY_LABEL: Record<DefectSeverity, string> = { critical: "K1", major: "K2", minor: "K3" };
export const SEVERITIES: DefectSeverity[] = ["critical", "major", "minor"];
export const STATUSES: DefectStatus[] = ["reported", "open", "resolved", "closed", "discarded"];
/** Was als offen zählt — dieselbe Menge wie `OPEN_DEFECT_STATUSES` im Server. */
export const OPEN_STATUSES: DefectStatus[] = ["reported", "open"];
/** `confirmedByFollowUp` setzt nur die Folge-Checkliste, nie ein Mensch im Portal. */
export const MANUAL_MEASURE_KINDS: MeasureKind[] = ["repair", "immediate", "provisional", "safeguard"];
/** Betriebszustand der Anlage am Mangel — dieselbe Menge wie `OPERATIONS` im Server. */
export const OPERATIONS: DefectOperation[] = ["normal", "restricted", "outOfService"];

/**
 * Die Auswahl „Status" der Liste. `openSet` und `overdue` sind keine Status, sondern Mengen, die der
 * Server selbst bildet (`open=true`, `overdue=true`); `all` heißt ohne Einschränkung. Die Menge
 * „Alle offenen" heißt bewusst nicht `open`: Das ist schon der Einzelstatus, und zwei Einträge mit
 * demselben Wert zeigte der Select doppelt und hakte beide an.
 */
export type StatusChoice = "all" | "openSet" | "overdue" | DefectStatus;
export const STATUS_CHOICES: StatusChoice[] = ["openSet", "overdue", "all", ...STATUSES];

export interface DefectFilter {
  status: StatusChoice;
  /** Nur die, die mir zugewiesen sind (`assignee=me`). */
  mine: boolean;
  site?: string;
  building?: string;
}

/** Die Vorgabe: offene Mängel — was jemand zuerst sehen will, der die Liste öffnet. */
export const DEFAULT_FILTER: DefectFilter = { status: "openSet", mine: false };

/** Adressparameter des Panels; die Seite der Einstellungen trägt daneben `panel`. */
export const FILTER_PARAMS = { status: "status", mine: "mine", site: "site", building: "building" } as const;

export const filterFromSearchParams = (params: URLSearchParams): DefectFilter => {
  const status = params.get(FILTER_PARAMS.status) as StatusChoice | null;

  return {
    status: status && STATUS_CHOICES.includes(status) ? status : DEFAULT_FILTER.status,
    mine: params.get(FILTER_PARAMS.mine) === "1",
    site: params.get(FILTER_PARAMS.site) || undefined,
    building: params.get(FILTER_PARAMS.building) || undefined,
  };
};

/** Schreibt den Filter in die Adresse; ein Gebäude ohne Liegenschaft gibt es in der Auswahl nicht. */
export const applyFilterToSearchParams = (current: URLSearchParams, filter: DefectFilter): URLSearchParams => {
  const params = new URLSearchParams(current);
  const set = (key: string, value: string | undefined) => (value ? params.set(key, value) : params.delete(key));

  set(FILTER_PARAMS.status, filter.status === DEFAULT_FILTER.status ? undefined : filter.status);
  set(FILTER_PARAMS.mine, filter.mine ? "1" : undefined);
  set(FILTER_PARAMS.site, filter.site);
  set(FILTER_PARAMS.building, filter.site ? filter.building : undefined);

  return params;
};

/** Die Abfrage für `GET /:customer/defects` — die Parameter heißen wie in `filterFromQuery` des Servers. */
export const defectQuery = (filter: DefectFilter): string => {
  const params = new URLSearchParams();

  if (filter.status === "openSet") params.set("open", "true");
  else if (filter.status === "overdue") params.set("overdue", "true");
  else if (filter.status !== "all") params.set("status", filter.status);

  if (filter.mine) params.set("assignee", "me");
  if (filter.site) params.set("site", filter.site);
  if (filter.site && filter.building) params.set("building", filter.building);

  return params.toString();
};

export interface Viewer {
  userId: string;
  /** Disponent: Kunden- oder technischer Administrator (bzw. System-Admin) — wie `actorOf` im Server. */
  dispatcher: boolean;
}

/** Disponent wie im Server (`hasCustomerRole`: System-Admin, Kunden- oder technischer Administrator). */
export const isDispatcher = (access: { isSystemAdmin: boolean; isCustomerAdmin: boolean; isTechnicalAdmin: boolean }) =>
  access.isSystemAdmin || access.isCustomerAdmin || access.isTechnicalAdmin;

/**
 * Die Aktionen, die dieser Betrachter an diesem Mangel ausführen darf, in Anzeigereihenfolge.
 *
 * `setDeadline` zeigt sich auch dem Verantwortlichen: Er darf die Frist verkürzen, verlängern darf
 * nur der Disponent — das unterscheidet erst der eingegebene Wert, also prüft es der Server.
 */
export const availableActions = (defect: Pick<Defect, "status" | "assignee">, viewer: Viewer): DefectAction[] => {
  const open = OPEN_STATUSES.includes(defect.status);
  const isAssignee = !!viewer.userId && defect.assignee === viewer.userId;
  const handles = viewer.dispatcher || isAssignee;
  const actions: DefectAction[] = [];

  if (viewer.dispatcher && defect.status === "reported") actions.push("triage");
  if (viewer.dispatcher && open) actions.push("assign");
  if (handles && open) actions.push("setDeadline");
  if (handles && open) actions.push("resolve");
  if (viewer.dispatcher && defect.status === "resolved") actions.push("close", "reopen");
  if (viewer.dispatcher && open) actions.push("discard");
  actions.push("comment");

  return actions;
};

export interface MaterialDraft {
  name: string;
  quantity: string;
  unit: string;
  billable: boolean;
}

/** Was die Formulare der Aktionen sammeln — ein Zustand für alle, jede Aktion liest ihren Teil. */
export interface ActionDraft {
  severity?: DefectSeverity;
  assignee?: string;
  /** Wert eines `datetime-local`-Feldes, Ortszeit. */
  deadline?: string;
  reason?: string;
  comment?: string;
  measureKind?: MeasureKind;
  measureDescription?: string;
  materials?: MaterialDraft[];
}

const trimmed = (value: string | undefined) => (value ?? "").trim();

const materialsOf = (draft: ActionDraft) =>
  (draft.materials ?? [])
    .filter((m) => trimmed(m.name) || trimmed(m.quantity) || trimmed(m.unit))
    .map((m) => ({
      name: trimmed(m.name),
      quantity: Number(trimmed(m.quantity).replace(",", ".")),
      unit: trimmed(m.unit),
      billable: m.billable,
    }));

/** Eine angefangene Materialzeile ist nur gültig, wenn Name, positive Menge und Einheit dastehen. */
const materialsValid = (draft: ActionDraft) =>
  materialsOf(draft).every((m) => m.name && Number.isFinite(m.quantity) && m.quantity > 0 && m.unit);

const hasMeasureInput = (draft: ActionDraft) => !!trimmed(draft.measureDescription) || materialsOf(draft).length > 0;

/**
 * Darf die Aktion abgeschickt werden? Dieselben Pflichtangaben wie der Server — sonst käme nur ein
 * 400 zurück, und das Formular wüsste nicht, welches Feld fehlt.
 */
export const canSubmit = (action: DefectAction, draft: ActionDraft, defect: Pick<Defect, "measures">): boolean => {
  switch (action) {
    case "triage":
      return true;
    case "assign":
      return !!trimmed(draft.assignee);
    case "setDeadline":
      return !!trimmed(draft.deadline) && !Number.isNaN(new Date(draft.deadline!).getTime()) && !!trimmed(draft.reason);
    case "resolve":
      // Beheben braucht eine Maßnahme — eine neue oder eine schon nachgetragene.
      return materialsValid(draft) && (hasMeasureInput(draft) || (defect.measures?.length ?? 0) > 0);
    case "reopen":
    case "discard":
      return !!trimmed(draft.reason);
    case "close":
      return true;
    case "comment":
      return !!trimmed(draft.comment);
    default:
      return false;
  }
};

/** Der Körper für `POST …/actions/:action` — nur die Felder, die die Aktion liest. */
export const actionPayload = (action: DefectAction, draft: ActionDraft): Record<string, unknown> => {
  switch (action) {
    case "triage":
      return {
        ...(draft.severity ? { severity: draft.severity } : {}),
        ...(trimmed(draft.assignee) ? { assignee: trimmed(draft.assignee) } : {}),
      };
    case "assign":
      return { assignee: trimmed(draft.assignee) };
    case "setDeadline":
      return { deadline: new Date(draft.deadline!).toISOString(), reason: trimmed(draft.reason) };
    case "resolve":
      return hasMeasureInput(draft)
        ? {
            measure: {
              kind: draft.measureKind ?? "repair",
              description: trimmed(draft.measureDescription) || undefined,
              materials: materialsOf(draft),
            },
          }
        : {};
    case "reopen":
    case "discard":
      return { reason: trimmed(draft.reason) };
    case "close":
      return {};
    case "comment":
      return { comment: trimmed(draft.comment) };
    default:
      return {};
  }
};

/**
 * Was der Dialog „Mangel anlegen" sammelt. Angelegt wird nur vom Disponenten: Der Server nimmt
 * dann seine Klasse und seinen Verantwortlichen an und setzt den Mangel sofort auf `open`, mit der
 * Frist aus der Fristtabelle (`buildNewDefect` in tri-server).
 */
export interface NewDefectDraft {
  /** Mongo-Id der Anlage im Mandanten. */
  asset?: string;
  title?: string;
  description?: string;
  severity?: DefectSeverity;
  assignee?: string;
  operation: DefectOperation;
}

export const EMPTY_NEW_DEFECT: NewDefectDraft = { severity: "major", operation: "normal" };

/** Dieselben Pflichtangaben wie der Server: Anlage, Klasse und Titel oder Beschreibung. */
export const canCreateDefect = (draft: NewDefectDraft): boolean =>
  !!trimmed(draft.asset) && !!draft.severity && (!!trimmed(draft.title) || !!trimmed(draft.description));

/** Der Körper für `POST /:customer/defects` — leere Felder fehlen, statt leer mitzugehen. */
export const createDefectPayload = (draft: NewDefectDraft): Record<string, unknown> => ({
  asset: trimmed(draft.asset),
  ...(trimmed(draft.title) ? { title: trimmed(draft.title) } : {}),
  ...(trimmed(draft.description) ? { description: trimmed(draft.description) } : {}),
  severity: draft.severity,
  operation: draft.operation,
  ...(trimmed(draft.assignee) ? { assignee: trimmed(draft.assignee) } : {}),
});

/** Die Anlage in der Auswahl: Name, dahinter die Kennung, sonst die Id. */
export const assetOptionLabel = (asset: { _id?: any; name?: string | null; id?: string | null }): string => {
  const name = (asset.name ?? "").trim();
  const id = (asset.id ?? "").trim();
  if (name && id && name !== id) return `${name} (${id})`;

  return name || id || String(asset._id ?? "—");
};

/** Der Titel für Liste und Kopf. Ältere Checklisten-Mängel (vor 26.10.2026) tragen nur `description`. */
export const defectTitle = (defect: Pick<Defect, "title" | "description">, max = 120): string => {
  const title = (defect.title ?? "").trim();
  if (title) return title;
  const line = (defect.description ?? "").trim().split(/\r?\n/)[0].trim();
  if (!line) return "—";

  return line.length > max ? `${line.slice(0, max - 1).trimEnd()}…` : line;
};

/** Datum und Uhrzeit ohne Sekunden, im Deutschen `dd.mm.yyyy, HH:mm`. */
export const formatDateTime = (value: unknown, locale?: string): string => {
  if (!value) return "—";
  const date = new Date(value as any);

  return Number.isNaN(date.getTime())
    ? "—"
    : date.toLocaleString(locale, { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

/** Ein Datum als Wert eines `datetime-local`-Feldes (Ortszeit, Minuten). */
export const toDateTimeLocal = (value: string | Date | null | undefined): string => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

/**
 * Die Adresse eines Fotos. Die App speichert, was `PUT …/photo` zurückgibt: `/documents/<Schlüssel>`,
 * server-relativ. Das löst `resolve` auf — im Panel `userAPI.getResourceURL`, derselbe Helfer wie für
 * die übrigen Anlagenfotos (ein voller Link bleibt, sonst Serveradresse davor). Ein nackter
 * Speicherschlüssel ist nur der Rückfall und bekommt Route und Mandanten davor. Gelesen wird über das
 * Datei-Cookie (tri-server docs/documents-route-access.md), nicht über den Bearer-Header.
 */
export const photoUrl = (path: string, customer: string, resolve: (url: string) => string): string => {
  if (/^https?:\/\//i.test(path) || path.startsWith("/documents/")) return resolve(path);
  const clean = path.replace(/^\/+/, "");
  const withTenant = clean.startsWith(`${customer}/`) ? clean : `${customer}/${clean}`;

  return resolve(`/documents/${withTenant.split("/").map(encodeURIComponent).join("/")}`);
};

export interface UserLabels {
  /** Ein Systemaufrufer (`system:…`). */
  system: string;
  /** Die Dokumentanalyse (`documentAnalysis`, tri-server `buildDocumentDefect`); ohne Angabe „System". */
  documentAnalysis?: string;
  /** Eine echte Id, die die Nutzerliste des Mandanten nicht kennt. */
  unknown: string;
  /** Solange die Nutzerliste lädt. */
  loading: string;
  /**
   * Ein leerer Wert. Wer handelt, ist ohne Person das System („System"); wer verantwortlich ist,
   * ist ohne Person niemand („—").
   */
  empty: string;
}

type UserRow = { _id?: any; id?: string; firstName?: string; lastName?: string; email?: string };

/**
 * Anzeigename eines Nutzers — nie die rohe Id. `users` ist `null`, solange die Liste lädt.
 * Eine Id, die die Liste nicht auflöst, heißt „Unbekannt", nicht „System": „System hat
 * zugewiesen" wäre für eine Disponentin eine falsche Auskunft.
 */
export const userLabel = (users: Record<string, UserRow> | null, id: string | null | undefined, labels: UserLabels): string => {
  if (!id) return labels.empty;
  if (id.startsWith("system")) return labels.system;
  if (id === "documentAnalysis") return labels.documentAnalysis ?? labels.system;
  if (!users) return labels.loading;
  const user = users[id];
  if (!user) return labels.unknown;
  const name = [user.firstName, user.lastName].filter(Boolean).join(" ").trim();

  return name || user.email || labels.unknown;
};

/**
 * Die Mongo-Id des Angemeldeten — dieselbe, die der Server vergleicht (`String(user._id)` in
 * `actorOf`). Nach dem Login trägt die Anmeldung `_id`; nach einem Neuladen kommt sie aus dem Token
 * (`populateAuthenticationFromToken`), und dort fehlt `_id`, während `id` die Geschäfts-Id
 * (`user.id`) sein kann. Dann über die Nutzerliste: Geschäfts-Id, sonst E-Mail.
 */
export const viewerUserId = (
  authentication: { _id?: any; id?: string; email?: string } | null | undefined,
  users: Record<string, UserRow> | null,
): string => {
  if (authentication?._id) return String(authentication._id);
  if (!users) return "";
  const match = Object.entries(users).find(
    ([mongoId, user]) =>
      (!!authentication?.id && (user.id === authentication.id || mongoId === authentication.id)) ||
      (!!authentication?.email && user.email === authentication.email),
  );

  return match ? match[0] : "";
};
